import { Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedColumns } from "store/slices/techData/techDataSlice";

import { IsAllowed } from "../../acl";
import { resources } from "../../acl/resources";
import {
  useAddChildTableRow,
  useAddParentTableRow,
  useDeleteChildTableRow,
  useDeleteParentTableRow,
  useModifyChildTableCell,
  useModifyParentTableCell,
} from "../../api/hooks/allHooks";
import { techDataSections } from "../../configs/TechnicalDataEnums";
import {
  getChildTableDataKey,
  getTechTableConfig,
  withHotWaterDecentralTypeCleared,
} from "../../configs/TechnicalDataUtils";
// import { useLinkedTechSystems } from "../../features/technical-data/lib/useLinkedTechSystems";
import { getCustomTechDataTabComponent } from "../../features/technical-data/technicalDataTabRegistry";
import { eventNames, trackGAEvent } from "../../functions/googleAnalytics";
import i18next from "../../i18n";
import { parseLocaleStringBasedOnUser } from "../../utils/namesManipulation";
import { detailsCellHeader } from "../DetailsCellHeader";
import { LoadingOrEmptyComponent } from "../LoadingAndEmptyHandler";
import { Table } from "../Table";
import EditTechDataRowForm from "./EditTechDataRow";
import { TableContainerHeader } from "./TableContainerHeader";

const LegacyTechTable = ({ activeTab, data, enabledTabs, parentsAreLoading, projectId }) => {
  // Don't remove this useTranslation() as the component needs to be rerendered on language change
  useTranslation();
  const { user } = useSelector((store) => store.user);
  const { colNames: userColumns, columnsIsLoading } = useSelector((store) => store.techData);

  const canDownload = IsAllowed("TECHNICAL_DATA.DOWNLOAD_FILES");
  const editable = IsAllowed(resources.TECHNICAL_DATA.EDIT_TABLE_CELL);
  const removeable = IsAllowed(resources.TECHNICAL_DATA.REMOVE_ROW);

  const [selectedRow, setSelectedRow] = useState(null);

  const dispatch = useDispatch();
  const { mutate: parentMutate } = useModifyParentTableCell();
  const { mutate: parentAddNewRow } = useAddParentTableRow();
  const { mutate: childMutate } = useModifyChildTableCell();
  const { mutate: childAddNewRow } = useAddChildTableRow();
  const { mutate: parentDeleteRow } = useDeleteParentTableRow();
  const { mutate: childDeleteRow } = useDeleteChildTableRow();

  // const { heating: heatingSystemOptions } = useLinkedTechSystems(
  //   projectId,
  //   activeTab === techDataSections.HOT_WATER
  // );
  const config = getTechTableConfig({ activeTab, projectId });
  const cols = config[activeTab].columns;
  const allowedCols = cols?.filter((col) => {
    return canDownload || col.cellType !== "downloadZipFile";
  });

  const colsState = userColumns[config[activeTab].dataKey];
  const filteredCols = allowedCols?.filter((col) => {
    return colsState?.includes(col.dataKey) && (canDownload || col.cellType !== "downloadZipFile");
  });

  const childCols = config.children.columns;
  const defaultChildCols = childCols?.filter((col) => {
    return col.isInDefault && (canDownload || col.cellType !== "downloadFile");
  });

  useEffect(() => {
    dispatch(getSelectedColumns({ projectId, userId: user?.user_id }));
  }, [dispatch, projectId, user?.user_id]);

  const parentHandleBlur = ({ fieldName, rowId, value }) => {
    const newValue = parseLocaleStringBasedOnUser(value);
    const data = { [fieldName]: newValue };
    parentMutate({
      activeTab,
      data: activeTab === techDataSections.HOT_WATER ? withHotWaterDecentralTypeCleared(data) : data,
      projectId,
      rowId,
    });
  };

  const parentHandleDelete = ({ rowId }) => {
    parentDeleteRow({ activeTab, projectId, rowId });
  };

  const parentHandleEdit = ({ row }) => {
    setSelectedRow(row);
  };

  const parentHandleAddNewRow = ({ fieldName, value }) => {
    const newValue = parseLocaleStringBasedOnUser(value);
    parentAddNewRow({
      activeTab,
      data: { [fieldName]: newValue, project: Number(projectId), projectId: Number(projectId) },
      projectId,
    });
  };

  const childHandleBlur = ({ fieldName, parentRowId, rowId, value }) => {
    const newValue = parseLocaleStringBasedOnUser(value);
    childMutate({
      activeTab,
      data: { [fieldName]: newValue },
      layerId: rowId,
      projectId,
      rowId: parentRowId,
    });
  };

  const childHandleAddNewRow = ({ fieldName, parentRow, value }) => {
    const newValue = parseLocaleStringBasedOnUser(value);
    childAddNewRow({
      activeTab,
      data: { [config[activeTab].keyInChildInsetionBody]: parentRow?.id, [fieldName]: newValue },
      projectId,
      rowId: parentRow?.id,
    });
  };

  const childHandleDelete = ({ parentRowId, rowId }) => {
    childDeleteRow({ activeTab, layerId: rowId, projectId, rowId: parentRowId });
  };

  const isAddableOrEditableInLanguage = editable && i18next.language === "en";

  const filteredColsWithDetailsBtn = [
    ...(filteredCols ?? []),
    detailsCellHeader(undefined, (row) => setSelectedRow(row)) ?? {},
  ];

  const tableRowsIdColumnName = "id";
  const colsWithoutDownloadZipFileAndId = cols?.filter((col) => {
    return col.cellType !== "downloadZipFile" && col.dataKey !== tableRowsIdColumnName;
  });
  return (
    <>
      {enabledTabs?.length > 0 && (
        <>
          <TableContainerHeader activeTab={activeTab} projectId={projectId} selectedColumns={colsState} />
          {!parentsAreLoading && !columnsIsLoading && (
            <Table
              addable={isAddableOrEditableInLanguage}
              childEditable={() => isAddableOrEditableInLanguage}
              childHeaders={defaultChildCols}
              childIdColumnName="id"
              childNewRowDataKey={config?.children?.newRowDataKey}
              childOnAddNewRow={childHandleAddNewRow}
              childOnBlur={childHandleBlur}
              childOnDelete={childHandleDelete}
              childTableDataKey={getChildTableDataKey(activeTab)}
              className="relative mt-6"
              columnsMinWidth="10rem"
              data={[...data]}
              editable={() => isAddableOrEditableInLanguage}
              foldTableOnChangeOfVariable={activeTab}
              headers={filteredColsWithDetailsBtn}
              idColumnName={tableRowsIdColumnName}
              newRowDataKey={config?.[activeTab]?.newRowDataKey}
              onAddNewRow={parentHandleAddNewRow}
              onBlur={parentHandleBlur}
              onDelete={parentHandleDelete}
              onEdit={parentHandleEdit}
              onExpand={() => trackGAEvent(eventNames.TD_EXPAND_FOLD_ROW)}
              rowRemoveable={() => removeable}
            />
          )}
          <EditTechDataRowForm
            activeTab={activeTab}
            projectId={projectId}
            row={selectedRow}
            setSelectedRow={setSelectedRow}
            tableHeaders={colsWithoutDownloadZipFileAndId}
          />
        </>
      )}
      <LoadingOrEmptyComponent
        showEmpty={!(enabledTabs?.length > 0)}
        showLoading={parentsAreLoading || columnsIsLoading}
      />
    </>
  );
};

export const TechTable = ({ activeTab, data, enabledTabs, parentsAreLoading, projectId }) => {
  const CustomTabComponent = getCustomTechDataTabComponent(activeTab);

  return (
    <Paper className="relative min-h-50 w-full">
      {CustomTabComponent ? (
        <CustomTabComponent projectId={projectId} />
      ) : (
        <LegacyTechTable
          activeTab={activeTab}
          data={data}
          enabledTabs={enabledTabs}
          parentsAreLoading={parentsAreLoading}
          projectId={projectId}
        />
      )}
    </Paper>
  );
};
